import React from 'react';
import { Link } from 'react-router-dom';
import { Image, Video, MapPin, Star, Sparkles } from 'lucide-react';
import { serviceLocations, galleryItems } from '../data/entoridMock';
import { SERVICES, CATEGORIES, getServicesByCategory } from '../data/servicesData';

const MegaMenu = ({ type, isOpen, onItemClick }) => {
  if (!isOpen) return null;

  const handleItemClick = () => {
    if (onItemClick) onItemClick();
  };

  const renderServiceColumn = (category, withPricingHint) => {
    const items = getServicesByCategory(category.id);
    const linkPrefix = withPricingHint ? '/pricing' : '/services';
    return (
      <div key={`${category.id}-${withPricingHint ? 'pricing' : 'services'}`}>
        <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider">{category.label}</h3>
        <ul className="space-y-2.5">
          {items.map((svc) => (
            <li key={svc.slug}>
              <Link
                to={`${linkPrefix}/${svc.slug}`}
                onClick={handleItemClick}
                className="flex items-start gap-3 transition-colors group rounded-md px-2 py-1.5 -mx-2 hover:bg-green-50"
                data-testid={`mega-link-${withPricingHint ? 'pricing-' : ''}${svc.slug}`}
              >
                <Sparkles size={16} className="mt-1 text-green-600 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-900 group-hover:text-green-700 text-[14px]">{svc.name}</div>
                  {withPricingHint && (
                    <div className="text-xs text-gray-500 mt-0.5 group-hover:text-green-600">View Pricing →</div>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderServicesMenu = () => (
    <div className="grid grid-cols-4 gap-6">
      {CATEGORIES.map(cat => renderServiceColumn(cat, false))}
    </div>
  );

  const renderPricingMenu = () => (
    <div className="grid grid-cols-3 gap-6">
      {CATEGORIES.filter(c => c.id !== 'sectors').map(cat => renderServiceColumn(cat, true))}
    </div>
  );

  const renderGalleryMenu = () => (
    <div className="grid grid-cols-2 gap-8">
      <Link
        to="/gallery/images"
        onClick={handleItemClick}
        className="flex items-start gap-4 p-5 rounded-xl hover:bg-emerald-50 transition-colors group ring-1 ring-gray-100 hover:ring-emerald-200"
        data-testid="mega-gallery-images"
      >
        <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-200 flex-shrink-0">
          <Image size={24} />
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold text-gray-900 group-hover:text-emerald-700">Before & After Images</h4>
          <p className="text-sm text-gray-600 mt-0.5">Drag-to-compare gallery — termite, cockroach, bed bugs &amp; more.</p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            View Gallery →
          </span>
        </div>
      </Link>
      <Link
        to="/gallery/videos"
        onClick={handleItemClick}
        className="flex items-start gap-4 p-5 rounded-xl hover:bg-blue-50 transition-colors group ring-1 ring-gray-100 hover:ring-blue-200"
        data-testid="mega-gallery-videos"
      >
        <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center group-hover:bg-blue-200 flex-shrink-0">
          <Video size={24} />
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold text-gray-900 group-hover:text-blue-700">Treatment Videos</h4>
          <p className="text-sm text-gray-600 mt-0.5">Watch our certified technicians at work.</p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
            Watch Now →
          </span>
        </div>
      </Link>
    </div>
  );

  const renderLocationsMenu = () => (
    <div>
      <h3 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wider flex items-center gap-2">
        <MapPin size={18} className="text-green-600" />
        Service Areas in Uttarakhand
      </h3>
      <div className="grid grid-cols-5 gap-4">
        {serviceLocations.map((location) => (
          <a
            key={location}
            href="#"
            onClick={handleItemClick}
            className="text-gray-700 hover:text-green-600 transition-colors py-2 px-3 rounded hover:bg-green-50"
            data-testid={`mega-location-${location.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {location}
          </a>
        ))}
      </div>
    </div>
  );

  const renderTestimonialsMenu = () => (
    <div className="grid grid-cols-2 gap-8">
      <Link to="/testimonials/reviews" onClick={handleItemClick} className="flex items-center gap-4 p-4 rounded-lg hover:bg-green-50 transition-colors group" data-testid="mega-testimonials-reviews">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200">
          <Star size={24} className="text-green-600" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900">Customer Reviews</h4>
          <p className="text-sm text-gray-600">150+ verified reviews from Uttarakhand</p>
        </div>
      </Link>
      <Link to="/testimonials/videos" onClick={handleItemClick} className="flex items-center gap-4 p-4 rounded-lg hover:bg-green-50 transition-colors group" data-testid="mega-testimonials-videos">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200">
          <Video size={24} className="text-green-600" />
        </div>
        <div>
          <h4 className="font-semibold text-gray-900">Video Testimonials</h4>
          <p className="text-sm text-gray-600">Watch customer experiences</p>
        </div>
      </Link>
    </div>
  );

  const menuContent = {
    services: renderServicesMenu(),
    pricing: renderPricingMenu(),
    gallery: renderGalleryMenu(),
    locations: renderLocationsMenu(),
    testimonials: renderTestimonialsMenu(),
  };

  return (
    <div
      className="absolute left-0 right-0 top-full bg-white shadow-2xl border-t border-gray-100 z-[60] animate-fadeIn"
      data-testid={`mega-menu-${type}`}
    >
      <div className="max-w-7xl mx-auto px-8 py-8">
        {menuContent[type]}
      </div>
    </div>
  );
};

export default MegaMenu;
